/**
 * Publicar o pinout gráfico — a marca "pinout" do produto, trazida para onde se
 * trabalha no gráfico.
 *
 * A produção não achava como publicar o pinout gráfico (relatório de
 * 28/09/2026). O gesto existia, mas escondido: é a marca `publicado_pinout` de
 * cada produto do projeto, na aba Projeto → Produtos. Aqui ela ganha um botão
 * por produto e diz, em palavras, o que o cliente vê hoje.
 *
 * **O que põe o pinout no site** (a regra do `DadoDB`, que o site usa): o
 * produto publicado, com a data de publicação até hoje, e a marca de pinout. E
 * o componente precisa estar ligado a um sistema (aba Sistemas) — sem isso ele
 * não aparece, por mais publicado que o produto esteja. A tela avisa quando há
 * componente nessa situação.
 *
 * **E o modelo precisa estar publicado.** O pinout de um componente aparece nos
 * modelos que a restrição dos pinouts dele alcança, e só nos publicados. Isso
 * era um modelo de cada vez, na aba Modelos; aqui basta o componente escolhido
 * na barra — um botão publica todos os modelos que ele alcança.
 */

import { Link } from "react-router-dom"
import {
  pinoutNoAr, temData, usarProdutosDoProjeto, usarSalvarProduto, type ProdutoDoProjeto,
} from "@/api/produtosDoProjeto"
import { usarResumoDosVinculos } from "@/api/vinculosDeSistema"
import { usarModelosDoComponente, usarPublicarModelosDoComponente } from "@/api/modelos"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"

const dia = (d: string) => {
  const [a, m, x] = d.slice(0, 10).split("-")
  return `${x}/${m}/${a}`
}

function situacao(p: ProdutoDoProjeto): { texto: string; cor: string } {
  if (pinoutNoAr(p)) return { texto: "pinout gráfico no ar", cor: "var(--ok-on-soft, #2e7d32)" }
  if (p.publicado && p.publicadoPinout && temData(p.publicadoData))
    return { texto: `entra no ar em ${dia(p.publicadoData)}`, cor: "var(--acao-on-soft)" }
  if (p.publicado) return { texto: "no ar só com os diagramas", cor: "var(--txt2)" }
  return { texto: "fora do ar", cor: "var(--txt3)" }
}

/** Resumo curto para o botão da faixa: "no ar em 1 de 2". */
export function usarResumoDaPublicacao(projetoId: number) {
  const produtos = usarProdutosDoProjeto(projetoId)
  const lista = produtos.data ?? []
  return { noAr: lista.filter((p) => pinoutNoAr(p)).length, total: lista.length, carregando: produtos.isPending }
}

export function PublicacaoDoPinout({ projetoId, componenteId, componente }: {
  projetoId: number
  /** O componente escolhido na barra do gráfico; 0 quando nenhum. */
  componenteId: number
  /** Como o componente aparece na barra, para a frase. */
  componente: string
}) {
  const produtos = usarProdutosDoProjeto(projetoId)
  const salvar = usarSalvarProduto(projetoId)
  const resumo = usarResumoDosVinculos(projetoId)
  const lista = produtos.data ?? []
  const semSistema = resumo.data ? resumo.data.componentes - resumo.data.componentesComSistema : 0

  const erro = salvar.error instanceof ErroDeTrava || salvar.error instanceof ErroDaApi
    ? salvar.error.message : salvar.error ? "Não foi possível gravar." : null

  return (
    <div
      className="barra"
      style={{
        flex: "0 0 auto", display: "flex", flexDirection: "column", gap: 8,
        padding: "10px 18px", background: "var(--surface)", borderBottom: "1px solid var(--line)",
      }}
    >
      <span style={{ fontSize: 11.5, color: "var(--txt2)", lineHeight: 1.5 }}>
        <b>Publicar o pinout gráfico</b> é por produto: o cliente de cada produto passa a ver o pinout
        gráfico interativo deste projeto. Tirar do ar mantém os diagramas publicados.
      </span>

      {produtos.isPending ? (
        <span style={{ fontSize: 12, color: "var(--txt3)" }}>Carregando os produtos…</span>
      ) : lista.length === 0 ? (
        <span style={{ fontSize: 12, color: "var(--txt2)" }}>
          Este projeto ainda não está em nenhum produto — sem produto não há onde publicar.{" "}
          <Link to={`/projetos/${projetoId}/projeto`} style={{ color: "var(--brand-on-soft)", fontWeight: 600 }}>
            Vincular um produto
          </Link>
        </span>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {lista.map((p) => {
            const s = situacao(p)
            const noAr = p.publicado && p.publicadoPinout
            return (
              <div key={p.produtoId} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "5px 9px", borderRadius: 7,
                background: "var(--surface2)", fontSize: 12.5,
              }}>
                <span style={{ flex: 1, minWidth: 0 }}>{p.produtoNome}</span>
                <span style={{ fontSize: 11.5, fontWeight: 600, color: s.cor }}>{s.texto}</span>
                <button
                  onClick={() => salvar.mutate(noAr
                    ? { produtoId: p.produtoId, publicado: p.publicado, publicadoPinout: false }
                    : { produtoId: p.produtoId, publicado: true, publicadoPinout: true })}
                  disabled={salvar.isPending}
                  style={{
                    height: 28, padding: "0 12px", borderRadius: 7, fontSize: 12, fontWeight: 600,
                    border: noAr ? "1px solid var(--line2)" : 0,
                    background: noAr ? "var(--surface)" : "var(--brand)",
                    color: noAr ? "var(--txt2)" : "#FFFFFF",
                  }}
                >
                  {noAr ? "Tirar o pinout do ar" : "Publicar pinout gráfico"}
                </button>
              </div>
            )
          })}
        </div>
      )}

      <ModelosDoComponente projetoId={projetoId} componenteId={componenteId} componente={componente} />

      {semSistema > 0 && (
        <span style={{ fontSize: 11.5, color: "var(--acao-on-soft)", lineHeight: 1.5 }}>
          {semSistema} de {resumo.data?.componentes} componentes não estão ligados a um sistema e não
          aparecem no site, mesmo publicados.{" "}
          <Link to={`/projetos/${projetoId}/sistemas`} style={{ color: "var(--brand-on-soft)", fontWeight: 600 }}>
            Ligar na aba Sistemas
          </Link>
        </span>
      )}

      {erro && <span role="alert" style={{ fontSize: 12, color: "var(--erro)" }}>{erro}</span>}
    </div>
  )
}

/**
 * Os modelos do componente escolhido e o botão que publica todos.
 *
 * Mostra a lista antes de publicar: é o que o botão vai mexer, e um modelo que
 * falta ali é restrição que falta no pinout — que se corrige no pinout, não aqui.
 */
function ModelosDoComponente({ projetoId, componenteId, componente }: {
  projetoId: number
  componenteId: number
  componente: string
}) {
  const modelos = usarModelosDoComponente(componenteId)
  const publicar = usarPublicarModelosDoComponente(projetoId)

  if (componenteId <= 0) {
    return (
      <span style={{ fontSize: 11.5, color: "var(--txt3)" }}>
        Escolha o componente na barra acima para publicar os modelos dele.
      </span>
    )
  }

  const lista = modelos.data ?? []
  const fora = lista.filter((m) => !m.publicado)
  const erro = publicar.error instanceof ErroDeTrava || publicar.error instanceof ErroDaApi
    ? publicar.error.message : publicar.error ? "Não foi possível publicar." : null

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingTop: 6,
                  borderTop: "1px solid var(--line2)" }}>
      <span style={{ fontSize: 11.5, color: "var(--txt2)", lineHeight: 1.5 }}>
        <b>Modelos de {componente || `#${componenteId}`}</b> — os que a restrição dos pinouts dele
        alcança. O pinout só aparece nos publicados.
      </span>

      {modelos.isPending ? (
        <span style={{ fontSize: 12, color: "var(--txt3)" }}>Carregando os modelos…</span>
      ) : modelos.isError ? (
        <span style={{ fontSize: 12, color: "var(--erro)" }}>Não foi possível carregar os modelos.</span>
      ) : lista.length === 0 ? (
        <span style={{ fontSize: 12, color: "var(--txt2)" }}>
          Nenhum pinout deste componente tem restrição que alcance um modelo — sem isso ele não
          aparece em modelo nenhum. Ponha a restrição nos pinouts.
        </span>
      ) : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {lista.map((m) => (
              <span key={m.id} title={m.publicado ? "Publicado" : "Fora do ar"} style={{
                fontSize: 11.5, padding: "2px 8px", borderRadius: 999,
                background: m.publicado ? "var(--ok-soft)" : "var(--surface2)",
                color: m.publicado ? "var(--ok-on-soft)" : "var(--txt2)",
              }}>
                {m.publicado ? "● " : "○ "}{m.descricao || `#${m.id}`}
              </span>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              onClick={() => publicar.mutate(componenteId)}
              disabled={publicar.isPending || fora.length === 0}
              style={{
                height: 28, padding: "0 12px", borderRadius: 7, fontSize: 12, fontWeight: 600, border: 0,
                background: fora.length === 0 ? "var(--surface2)" : "var(--brand)",
                color: fora.length === 0 ? "var(--txt3)" : "#FFFFFF",
              }}
            >
              {publicar.isPending ? "Publicando…"
                : fora.length === 0 ? "Todos os modelos já estão publicados"
                : `Publicar em ${fora.length === lista.length ? "todos os" : "mais"} ${fora.length} modelo${fora.length === 1 ? "" : "s"}`}
            </button>
            {publicar.data && (
              <span style={{ fontSize: 11.5, color: "var(--txt2)" }}>
                {publicar.data.publicados} modelo(s) publicado(s).
              </span>
            )}
          </div>
        </>
      )}

      {erro && <span role="alert" style={{ fontSize: 12, color: "var(--erro)" }}>{erro}</span>}
    </div>
  )
}
